import localStore from "../storageAPIs/localStore";
import sessionStore from "../storageAPIs/sessionStore";
import ThirdPartyCookieChecker from "./checkers/ThirdPartyCookieChecker";
import clearUrl from "./clearUrl";
import storeToken from "./storeToken";
import checkUserToken from "./checkUserToken";
import addLoginAnalytics from "./addLoginAnalytics";
import isPasswordlessFlow from "./isPasswordlessFlow";
import checkSSOSession from "./checkSSOSession";

const InitCheck = (config, state, funcs) => {
  const thirdPartyCookieCheck = async () => ThirdPartyCookieChecker(config, funcs).runCookieCheck();

  const getParsedHash = (parseParams) =>
    new Promise((resolve, reject) => {
      const parseCallback = (error, success) => (error ? reject(error) : resolve(success));
      funcs.authenticator.webAuth.parseHash(parseParams, parseCallback);
    });

  const initUserURL = async () => {
    if (typeof window === "undefined") return { result: false, callback: () => {} };
    const parseParams = {};
    if (isPasswordlessFlow()) {
      parseParams.state = window.localStorage.getItem("state");
      parseParams.nonce = window.localStorage.getItem("nonce");
      window.localStorage.removeItem("state");
      window.localStorage.removeItem("nonce");
    }
    if (config.responseHash) parseParams.hash = config.responseHash;

    try {
      const result = await getParsedHash(parseParams);
      return result;
    } catch (e) {
      if (e?.error) {
        funcs.logger.log("URL check error: ", e, "warning");
        if (e.error === "login_required") localStore.removeIdTokenData();
        else {
          if (e.error === "unauthorized")
            funcs.events.publish("tunnusErrorEvent", { ...e, pwdless: isPasswordlessFlow() });
          return { error: e };
        }
        sessionStore.removeOriginUrl();
      }
      return false;
    }
  };

  const shouldRefresh = (isNewLogin, email_verified, tokenExpired) =>
    !isNewLogin && (email_verified === false || tokenExpired);

  const resolveSessionType = (urlResult, storageResult) => {
    if (urlResult?.error) return "error";
    else if (urlResult) return "url";
    else if (storageResult) return "session";
    else return 0;
  };

  const resolveSession = async ([cookieResult, urlResult, storageResult]) => {
    const oldIdToken = localStore.getIdTokenData();
    state.cookiesEnabled = cookieResult;
    const someProvider = sessionStore.getSomeLoginFlow();
    const originUrl = sessionStore.getOriginUrl();

    let callback = null;
    let user = null;
    let result = null;
    let isNewLogin = false;

    const sessionType = resolveSessionType(urlResult, storageResult);
    if (typeof window === "undefined") callback = () => config.checkSessionCallback(false);
    else if (sessionType === "error") callback = () => config.loginCallback({ ...urlResult.error, originUrl }, null);
    else if (sessionType === "url") {
      const { idToken, idTokenPayload } = urlResult;
      user = funcs.loginFunctions.setUserSession(idTokenPayload, !oldIdToken);
      storeToken(idToken, urlResult);
      if (oldIdToken !== null && typeof oldIdToken !== "undefined") {
        const idToken = urlResult ? urlResult.idToken : localStore.getIdTokenData().idToken;
        callback = () => config.checkSessionCallback(user, idToken);
      } else {
        isNewLogin = true;
        const someProvider = idTokenPayload["https://almamedia.net/connection"];
        callback = addLoginAnalytics(
          config,
          funcs,
          user,
          someProvider
        )(user, () => {
          config.loginCallback(null, {
            accessToken: urlResult.accessToken,
            user,
            idToken: urlResult.idToken,
            originUrl,
          });
        });
      }
      sessionStore.removeOriginUrl();
      result = user;
    } else if (sessionType === "session") {
      result = storageResult;
      const idToken = localStore.getIdTokenData().idToken;
      user = funcs.loginFunctions.setUserSession(storageResult, false);
      callback = () => config.checkSessionCallback(user, idToken);
    } else callback = () => config.checkSessionCallback(false);

    if (!config.disableUrlClearing) clearUrl();
    sessionStore.removeSomeLoginFlow();

    const isRefresh = shouldRefresh(isNewLogin, result?.email_verified, state.tokenExpired);

    if (!result || isRefresh) {
      if (someProvider) funcs.analytics.sendSomeFailure(someProvider);
      if (cookieResult && isRefresh) {
        const ssoCheck = await checkSSOSession(config, state, funcs)(true, false);
        if (ssoCheck?.result) {
          callback = ssoCheck.callback;
        }
      }
      state.tokenExpired = false;
    }
    return callback;
  };

  return {
    thirdPartyCookieCheck,
    initUserURL,
    shouldRefresh,
    resolveSession,
    resolveSessionType,
    run: () => {
      Promise.all([thirdPartyCookieCheck(), initUserURL(), checkUserToken(state, funcs)(localStore.getIdTokenData())])
        .then(resolveSession)
        .then((callback) => callback());
    },
  };
};

export default InitCheck;
